import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, RefreshCw, Calendar, Unlock, Building2, Play, ChevronDown, Search, ChevronsDownUp, ChevronsUpDown, X, Layers, Lock, Clock, CalendarDays, Info, FileText, BarChart3 } from 'lucide-react';
import http from '../api/http';
import { useFiliale } from '../context/FilialeContext';
import BlueHeader from '../components/BlueHeader';

// Drapeau de la filiale (repli propre sur une icône si l'image est absente ou cassée)
const FilialeFlag = ({ src, alt, size = 'md' }) => {
  const [erreur, setErreur] = useState(false);
  const dims = size === 'md' ? 'h-9 w-12' : 'h-5 w-8';
  if (!src || erreur) {
    return (
      <span className={`flex ${dims} flex-shrink-0 items-center justify-center rounded-md bg-blue-50 text-blue-500 ring-1 ring-blue-100`}>
        <Building2 className={size === 'md' ? 'h-5 w-5' : 'h-4 w-4'} />
      </span>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      onError={() => setErreur(true)}
      className={`${dims} flex-shrink-0 rounded-md object-cover shadow-sm ring-1 ring-black/10`}
    />
  );
};

// Case à cocher avec état « partiel »
const TriCheckbox = ({ checked, indeterminate, onChange, label }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = Boolean(indeterminate) && !checked;
  }, [indeterminate, checked]);
  return (
    <input
      ref={ref}
      type="checkbox"
      aria-label={label}
      checked={checked}
      onChange={onChange}
      onClick={(e) => e.stopPropagation()}
      className="h-4 w-4 cursor-pointer rounded border-gray-300 text-blue-600 focus:ring-blue-500"
    />
  );
};

// ═══════════════════════════════════════════════════════════════════════
//  PRÉSENTATION — helpers et composants purement visuels
//  Aucune logique métier ni appel API ici : tout arrive par les props.
// ═══════════════════════════════════════════════════════════════════════

// Normalise un code filiale (ex. 'sn' -> 'SN') : évite les écarts de casse / d'espaces
const normaliserCode = (code) => (code ?? '').toString().trim().toUpperCase();

// Clé de regroupement d'une période : le CODE de la filiale (unique en base).
// Repli sur l'id uniquement si l'API ne renvoie pas le code.
const cleFiliale = (paie) => {
  const code = normaliserCode(paie.filiale_code);
  if (code) return `code:${code}`;
  return paie.filiale_id != null ? `id:${paie.filiale_id}` : 'none';
};
const idDom = (cle) => String(cle).replace(/[^a-zA-Z0-9_-]/g, '-');

const formatDateFr = (d) => {
  if (!d) return null;
  const date = new Date(d);
  return Number.isNaN(date.getTime()) ? null : date.toLocaleDateString('fr-FR');
};

// Durée inclusive en jours entre deux dates (null si une date manque ou est invalide)
const dureeJours = (debut, fin) => {
  if (!debut || !fin) return null;
  const d = new Date(debut);
  const f = new Date(fin);
  if (Number.isNaN(d.getTime()) || Number.isNaN(f.getTime())) return null;
  return Math.round((f.getTime() - d.getTime()) / 86400000) + 1;
};

const MOIS = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];

// « 1_1,15_6,1_5 » -> [{ label: '1 janv.' }, { label: '15 juin' }, { label: '1 mai' }]
const parseJoursFeries = (texte) =>
  String(texte || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
    .map((brut) => {
      const m = brut.match(/^(\d{1,2})_(\d{1,2})$/);
      const jour = m ? Number(m[1]) : 0;
      const mois = m ? Number(m[2]) : 0;
      if (m && jour >= 1 && jour <= 31 && mois >= 1 && mois <= 12) {
        return { brut, label: `${jour} ${MOIS[mois - 1]}`, valide: true };
      }
      return { brut, label: brut, valide: false };
    });

// Onglets d'état : même valeurs d'état que l'existant (0 non active, 1 ouverte, 2 clôturée)
const ONGLETS_ETAT = [
  { id: 'all', label: 'Toutes', icon: Layers, etat: null },
  { id: 'ouvertes', label: 'Ouvertes', icon: Unlock, etat: 1 },
  { id: 'non_actives', label: 'Non actives', icon: Clock, etat: 0 },
  { id: 'cloturees', label: 'Clôturées', icon: Lock, etat: 2 },
];
const ETAT_DOT = { 0: 'bg-gray-400', 1: 'bg-green-500', 2: 'bg-blue-500' };

const PaieStyles = () => (
  <style>{`
    @keyframes paieFadeUp { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }
    @keyframes paieModalIn { from { opacity: 0; transform: translateY(18px) scale(.97); } to { opacity: 1; transform: none; } }
    @keyframes paieFade { from { opacity: 0; } to { opacity: 1; } }
    @keyframes paieGrow { from { transform: scaleX(0); } to { transform: scaleX(1); } }
    .paie-fade-up { animation: paieFadeUp .38s cubic-bezier(.2,.8,.2,1) both; }
    .paie-modal-in { animation: paieModalIn .3s cubic-bezier(.2,.8,.2,1) both; }
    .paie-fade { animation: paieFade .25s ease-out both; }
    .paie-grow { transform-origin: left; animation: paieGrow .8s cubic-bezier(.2,.8,.2,1) both; }
    @media (prefers-reduced-motion: reduce) {
      .paie-fade-up, .paie-modal-in, .paie-fade, .paie-grow { animation: none; }
    }
  `}</style>
);

// Nombre qui « monte » jusqu'à sa valeur
const AnimatedNumber = ({ value, duration = 900 }) => {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    const target = Number(value) || 0;
    if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      setDisplay(target);
      return undefined;
    }
    let raf;
    let start;
    const step = (t) => {
      if (start === undefined) start = t;
      const k = Math.min(1, (t - start) / duration);
      setDisplay(Math.round(target * (1 - Math.pow(1 - k, 3))));
      if (k < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [value, duration]);
  return <>{display.toLocaleString('fr-FR')}</>;
};

// Onglets à indicateur glissant, compteurs, pastille « à compléter » et navigation clavier (← →)
const SlidingTabs = ({ tabs, active, onChange, label }) => {
  const listRef = useRef(null);
  const index = Math.max(0, tabs.findIndex((t) => t.id === active));

  const onKeyDown = (e) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    e.preventDefault();
    const next = (index + (e.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
    onChange(tabs[next].id);
    listRef.current?.querySelectorAll('[role="tab"]')[next]?.focus();
  };

  return (
    <div
      ref={listRef}
      role="tablist"
      aria-label={label}
      onKeyDown={onKeyDown}
      className="relative grid rounded-xl bg-gray-100 p-1"
      style={{ gridTemplateColumns: `repeat(${tabs.length}, minmax(0, 1fr))` }}
    >
      <span
        aria-hidden="true"
        className="absolute bottom-1 left-1 top-1 rounded-lg bg-white shadow transition-transform duration-300 ease-out"
        style={{ width: `calc((100% - 0.5rem) / ${tabs.length})`, transform: `translateX(${index * 100}%)` }}
      />
      {tabs.map((t) => {
        const Icon = t.icon;
        const selected = t.id === active;
        return (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={selected}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(t.id)}
            className={`relative z-10 flex items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors duration-200 ${
              selected ? 'text-nuit' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {Icon && <Icon className="h-4 w-4 flex-shrink-0" />}
            <span className="hidden truncate sm:inline">{t.label}</span>
            {t.badge !== undefined && (
              <span
                className={`rounded-full px-2 py-0.5 text-[11px] font-bold tabular-nums transition-colors duration-200 ${
                  selected ? 'bg-blue-100 text-blue-700' : 'bg-gray-200 text-gray-500'
                }`}
              >
                {t.badge}
              </span>
            )}
            {t.dot && (
              <span className="h-2 w-2 flex-shrink-0 rounded-full bg-amber-500" title="Champ obligatoire à renseigner" />
            )}
          </button>
        );
      })}
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, hint, gradient, progress, delay = 0 }) => {
  // La barre de progression se remplit à l'affichage
  const [on, setOn] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setOn(true));
    return () => cancelAnimationFrame(id);
  }, []);
  return (
    <div
      className="paie-fade-up relative overflow-hidden rounded-2xl bg-white p-5 shadow-sm ring-1 ring-gray-100 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
      style={{ animationDelay: `${delay}ms` }}
    >
      <div className={`absolute -right-8 -top-8 h-28 w-28 rounded-full bg-gradient-to-br opacity-10 ${gradient}`} />
      <div className="relative flex items-center gap-4">
        <div className={`flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br text-white shadow-lg ${gradient}`}>
          <Icon className="h-6 w-6" />
        </div>
        <div className="min-w-0">
          <div className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</div>
          <div className="truncate text-2xl font-bold tabular-nums text-nuit">
            <AnimatedNumber value={value} />
          </div>
          {hint ? <div className="truncate text-xs text-gray-400">{hint}</div> : null}
        </div>
      </div>
      {typeof progress === 'number' && (
        <div className="relative mt-4 h-1.5 overflow-hidden rounded-full bg-gray-100">
          <div
            className={`h-full rounded-full bg-gradient-to-r ${gradient}`}
            style={{ width: on ? `${progress}%` : '0%', transition: 'width 900ms cubic-bezier(.2,.8,.2,1)' }}
          />
        </div>
      )}
    </div>
  );
};

// Répartition ouvertes / non actives / clôturées d'une filiale
const RepartitionEtats = ({ ouvertes, nonActives, cloturees }) => {
  const total = ouvertes + nonActives + cloturees || 1;
  return (
    <div className="flex h-1.5 w-28 overflow-hidden rounded-full bg-gray-100" aria-hidden="true">
      <div className="paie-grow h-full bg-green-500" style={{ width: `${(ouvertes / total) * 100}%` }} />
      <div className="paie-grow h-full bg-amber-400" style={{ width: `${(nonActives / total) * 100}%` }} />
      <div className="paie-grow h-full bg-blue-500" style={{ width: `${(cloturees / total) * 100}%` }} />
    </div>
  );
};

const INPUT =
  'w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm transition-all focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20';
const LABEL = 'mb-1.5 block text-sm font-medium text-gray-700';

// Modale création / modification : onglets Période · Heures · Jours fériés + aperçu en direct
const PeriodeModal = ({ formData, setFormData, annees, editing, etatLabel, filialeCode, isInterim, onSubmit, onClose }) => {
  const [tab, setTab] = useState('periode');
  const formRef = useRef(null);
  const tabRef = useRef(tab);
  tabRef.current = tab;
  const premiereInvalide = useRef(false);

  const maj = (champ, valeur) => setFormData({ ...formData, [champ]: valeur });

  // Échap ferme la fenêtre (équivaut à « Annuler »)
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Focus sur le premier champ à l'ouverture
  useEffect(() => {
    formRef.current?.querySelector('#periode-libelle')?.focus();
  }, []);

  // Validation native : si le premier champ invalide est dans un onglet masqué, on bascule dessus
  useEffect(() => {
    const form = formRef.current;
    if (!form) return undefined;
    const onInvalid = (e) => {
      if (premiereInvalide.current) return;
      premiereInvalide.current = true;
      setTimeout(() => {
        premiereInvalide.current = false;
      }, 0);
      const cible = e.target.closest?.('[data-tab]')?.dataset.tab;
      if (cible && cible !== tabRef.current) {
        e.preventDefault();
        const champ = e.target;
        setTab(cible);
        setTimeout(() => {
          champ.focus?.();
          champ.reportValidity?.();
        }, 0);
      }
    };
    form.addEventListener('invalid', onInvalid, true);
    return () => form.removeEventListener('invalid', onInvalid, true);
  }, []);

  const debutFr = formatDateFr(formData.date_debut);
  const finFr = formatDateFr(formData.date_fin);
  const duree = dureeJours(formData.date_debut, formData.date_fin);
  const feries = parseJoursFeries(formData.jour_ferie);
  const anneeLibelle = annees.find((a) => String(a.idannee) === String(formData.annee_id))?.libelle_annee;
  const tpsPlein = parseFloat(formData.heure_tps_plein);
  const aPayer = parseFloat(formData.heure_a_payer);
  const ratio = tpsPlein > 0 && Number.isFinite(aPayer) ? Math.max(0, Math.min(100, (aPayer / tpsPlein) * 100)) : null;
  const manquePeriode = !formData.libelle_paie || !formData.date_debut || !formData.date_fin;
  const manqueHeures = String(formData.heure_tps_plein ?? '') === '' || String(formData.heure_a_payer ?? '') === '';

  const tabs = [
    { id: 'periode', label: 'Période', icon: CalendarDays, dot: manquePeriode },
    { id: 'heures', label: 'Heures', icon: Clock, dot: manqueHeures },
    { id: 'feries', label: 'Jours fériés', icon: Calendar, badge: feries.length > 0 ? feries.length : undefined },
  ];
  const panneau = (id, extra) => `${tab === id ? 'paie-fade-up' : 'hidden'} ${extra}`;

  return (
    <div className="paie-fade fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm" role="dialog" aria-modal="true" aria-labelledby="periode-modal-titre">
      <div className="paie-modal-in flex max-h-[92vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-blue-100">
        {/* En-tête */}
        <div className="flex items-center justify-between gap-4 border-b border-blue-100 bg-gradient-to-r from-blue-50 to-white px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-blue-600 to-indigo-600 text-white shadow-lg">
              <CalendarDays className="h-6 w-6" />
            </div>
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-widest text-blue-500">Configuration de la paie</div>
              <h3 id="periode-modal-titre" className="text-lg font-bold text-blue-900">
                {editing ? 'Modifier' : 'Créer'} une Période de Paie{isInterim ? ' Intérim' : ''}
              </h3>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer"
            className="rounded-full p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form ref={formRef} onSubmit={onSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="grid min-h-0 flex-1 overflow-y-auto md:grid-cols-[280px_1fr]">
            {/* Aperçu en direct */}
            <aside className="relative overflow-hidden bg-gradient-bleu p-6 text-white">
              <div className="pointer-events-none absolute -right-12 -top-16 h-52 w-52 rounded-full bg-white/10 blur-2xl" />
              <div className="pointer-events-none absolute -bottom-20 -left-10 h-44 w-44 rounded-full bg-fuchsia-400/20 blur-3xl" />
              <div className="relative">
                <div className="text-[11px] font-medium uppercase tracking-widest text-white/70">Aperçu</div>
                <div className={`mt-2 break-words text-xl font-bold leading-snug ${formData.libelle_paie ? '' : 'text-white/50'}`}>
                  {formData.libelle_paie || 'Nouvelle période'}
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5 text-[11px]">
                  {editing && etatLabel ? <span className="rounded-full bg-white/20 px-2.5 py-0.5 font-medium">{etatLabel}</span> : null}
                  {filialeCode ? <span className="rounded-full bg-white/20 px-2.5 py-0.5 font-semibold tracking-wide">{filialeCode}</span> : null}
                  {anneeLibelle ? <span className="rounded-full bg-white/20 px-2.5 py-0.5 font-medium">{anneeLibelle}</span> : null}
                </div>

                <div className="mt-6 rounded-2xl bg-white/10 p-4 ring-1 ring-white/20">
                  <div className="flex items-baseline justify-between">
                    <span className="text-[11px] uppercase tracking-wide text-white/70">Durée</span>
                    <span className="text-2xl font-bold tabular-nums">{duree && duree > 0 ? duree : '—'}<span className="ml-1 text-sm font-medium text-white/70">{duree && duree > 0 ? (duree > 1 ? 'jours' : 'jour') : ''}</span></span>
                  </div>
                  <div className="mt-3 flex items-center gap-2 text-sm">
                    <span className="rounded-lg bg-white/15 px-2.5 py-1 tabular-nums">{debutFr || '—'}</span>
                    <span className="text-white/60">→</span>
                    <span className="rounded-lg bg-white/15 px-2.5 py-1 tabular-nums">{finFr || '—'}</span>
                  </div>
                  {duree !== null && duree <= 0 ? (
                    <p className="mt-3 text-xs text-amber-200">La date de fin précède la date de début.</p>
                  ) : null}
                </div>

                <dl className="mt-5 space-y-3 text-sm">
                  <div className="flex items-center justify-between">
                    <dt className="text-white/70">Trimestre</dt>
                    <dd className="font-semibold">T{formData.trimestre_id}</dd>
                  </div>
                  <div className="flex items-center justify-between">
                    <dt className="text-white/70">Heures temps plein</dt>
                    <dd className="font-semibold tabular-nums">{formData.heure_tps_plein === '' ? '—' : formData.heure_tps_plein}</dd>
                  </div>
                  <div className="flex items-center justify-between">
                    <dt className="text-white/70">Heures à payer</dt>
                    <dd className="font-semibold tabular-nums">{formData.heure_a_payer === '' ? '—' : formData.heure_a_payer}</dd>
                  </div>
                  <div className="flex items-center justify-between">
                    <dt className="text-white/70">Jours fériés</dt>
                    <dd className="font-semibold tabular-nums">{feries.length}</dd>
                  </div>
                </dl>
              </div>
            </aside>

            {/* Formulaire à onglets */}
            <section className="min-w-0 p-6">
              <SlidingTabs tabs={tabs} active={tab} onChange={setTab} label="Sections de la période" />

              <div className="mt-6">
                {/* Période */}
                <div data-tab="periode" role="tabpanel" className={panneau('periode', 'grid-cols-1 gap-4 md:grid-cols-2')}>
                  <div className="md:col-span-2">
                    <label htmlFor="periode-libelle" className={LABEL}>Libellé de la période *</label>
                    <input
                      id="periode-libelle"
                      type="text"
                      value={formData.libelle_paie}
                      onChange={(e) => maj('libelle_paie', e.target.value)}
                      className={INPUT}
                      placeholder="Ex: Janvier 2025"
                      required
                    />
                  </div>

                  <div>
                    <span id="periode-trimestre-label" className={LABEL}>Trimestre *</span>
                    <div role="radiogroup" aria-labelledby="periode-trimestre-label" className="grid grid-cols-4 gap-2">
                      {[1, 2, 3, 4].map((n) => {
                        const actif = Number(formData.trimestre_id) === n;
                        return (
                          <button
                            key={n}
                            type="button"
                            role="radio"
                            aria-checked={actif}
                            aria-label={`Trimestre ${n}`}
                            onClick={() => maj('trimestre_id', n)}
                            className={`rounded-xl border py-3 text-sm font-semibold transition-all duration-200 ${
                              actif
                                ? 'border-blue-600 bg-gradient-to-br from-blue-600 to-indigo-600 text-white shadow-md'
                                : 'border-gray-200 bg-white text-gray-600 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700'
                            }`}
                          >
                            T{n}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div>
                    <label htmlFor="periode-annee" className={LABEL}>Année fiscale</label>
                    <select
                      id="periode-annee"
                      value={formData.annee_id}
                      onChange={(e) => maj('annee_id', e.target.value)}
                      className={INPUT}
                    >
                      <option value="">Sélectionner une année</option>
                      {annees.map((annee) => (
                        <option key={annee.idannee} value={annee.idannee}>{annee.libelle_annee}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="periode-debut" className={LABEL}>Date de début *</label>
                    <input
                      id="periode-debut"
                      type="date"
                      value={formData.date_debut}
                      onChange={(e) => maj('date_debut', e.target.value)}
                      className={INPUT}
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="periode-fin" className={LABEL}>Date de fin *</label>
                    <input
                      id="periode-fin"
                      type="date"
                      value={formData.date_fin}
                      onChange={(e) => maj('date_fin', e.target.value)}
                      className={INPUT}
                      required
                    />
                  </div>
                </div>

                {/* Heures / Jours */}
                <div data-tab="heures" role="tabpanel" className={panneau('heures', 'grid-cols-1 gap-4 md:grid-cols-2')}>
                  <div>
                    <label htmlFor="periode-tps-plein" className={LABEL}>{isInterim ? 'Jours temps plein' : 'Heure temps plein'} *</label>
                    <div className="relative">
                      <input
                        id="periode-tps-plein"
                        type="number"
                        step="0.01"
                        value={formData.heure_tps_plein}
                        onChange={(e) => maj('heure_tps_plein', e.target.value)}
                        className={`${INPUT} pr-10`}
                        placeholder={isInterim ? '22' : '173.33'}
                        required
                      />
                      <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-sm text-gray-400">{isInterim ? 'j' : 'h'}</span>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="periode-a-payer" className={LABEL}>{isInterim ? 'Jours à payer' : 'Heure à payer'} *</label>
                    <div className="relative">
                      <input
                        id="periode-a-payer"
                        type="text"
                        value={formData.heure_a_payer}
                        onChange={(e) => maj('heure_a_payer', e.target.value)}
                        className={`${INPUT} pr-10`}
                        placeholder={isInterim ? '22' : '173.33'}
                        required
                      />
                      <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-sm text-gray-400">{isInterim ? 'j' : 'h'}</span>
                    </div>
                  </div>

                  <div className="rounded-2xl border border-gray-100 bg-gray-50 p-4 md:col-span-2">
                    <div className="mb-2 flex items-center justify-between text-sm">
                      <span className="font-medium text-gray-600">Heures à payer / temps plein</span>
                      <span className="font-bold tabular-nums text-nuit">{ratio === null ? '—' : `${Math.round(ratio)}%`}</span>
                    </div>
                    <div className="h-2.5 overflow-hidden rounded-full bg-gray-200">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-blue-500 to-indigo-500"
                        style={{ width: `${ratio === null ? 0 : ratio}%`, transition: 'width 500ms cubic-bezier(.2,.8,.2,1)' }}
                      />
                    </div>
                  </div>
                </div>

                {/* Jours fériés */}
                <div data-tab="feries" role="tabpanel" className={panneau('feries', 'grid-cols-1 gap-4')}>
                  <div>
                    <label htmlFor="periode-feries" className={LABEL}>Jours fériés</label>
                    <input
                      id="periode-feries"
                      type="text"
                      value={formData.jour_ferie}
                      onChange={(e) => maj('jour_ferie', e.target.value)}
                      className={INPUT}
                      placeholder="ex: 1_1,15_6,1_5"
                    />
                    <p className="mt-1.5 flex items-center gap-1.5 text-xs text-gray-500">
                      <Info className="h-3.5 w-3.5 flex-shrink-0" />
                      Format jour_mois, séparés par des virgules (ex : 1_1 pour le 1er janvier).
                    </p>
                  </div>
                  <div className="min-h-[88px] rounded-2xl border border-dashed border-gray-200 bg-gray-50 p-4">
                    {feries.length === 0 ? (
                      <p className="text-sm text-gray-400">Aucun jour férié saisi pour cette période.</p>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {feries.map((f, i) => (
                          <span
                            key={`${f.brut}-${i}`}
                            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-medium ${
                              f.valide ? 'bg-blue-100 text-blue-800' : 'bg-red-50 text-red-600 ring-1 ring-red-100'
                            }`}
                            title={f.valide ? undefined : 'Format non reconnu (attendu : jour_mois)'}
                          >
                            <Calendar className="h-3.5 w-3.5" />
                            {f.label}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </section>
          </div>

          {/* Pied */}
          <div className="flex items-center justify-between gap-3 border-t border-blue-100 bg-white px-6 py-4">
            <span className="hidden text-xs text-gray-400 sm:inline">* Champs obligatoires</span>
            <div className="ml-auto flex gap-3">
              <button
                type="button"
                onClick={onClose}
                className="rounded-xl bg-gray-100 px-5 py-2.5 text-gray-700 transition-colors hover:bg-gray-200"
              >
                Annuler
              </button>
              <button
                type="submit"
                className="rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-2.5 font-medium text-white shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md active:translate-y-0"
              >
                {editing ? 'Modifier' : 'Créer'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

const ConfigPaie = () => {
  const navigate = useNavigate();
  const { filialeId, isConsolidated, currentFiliale } = useFiliale();
  const [paies, setPaies] = useState([]);
  const [paiesInterim, setPaiesInterim] = useState([]);
  const [annees, setAnnees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPaie, setEditingPaie] = useState(null);
  const [isInterim, setIsInterim] = useState(false);
  const [selectedPaies, setSelectedPaies] = useState([]);
  const [openGroups, setOpenGroups] = useState({});
  const [openGroupsInterim, setOpenGroupsInterim] = useState({});
  const [recherche, setRecherche] = useState('');
  const groupsInitialises = useRef(false);
  const [etatFiltre, setEtatFiltre] = useState('all');
  const [formData, setFormData] = useState({
    libelle_paie: '',
    trimestre_id: 1,
    date_debut: '',
    date_fin: '',
    heure_tps_plein: 173.33,
    heure_a_payer: '173.33',
    jour_ferie: '',
    annee_id: '',
    filiale_id: filialeId
  });

  const fetchPaies = useCallback(async () => {
    try {
      setLoading(true);
      let endpoint = '/api/paies';
      const params = [];

      if (!isConsolidated && filialeId && filialeId !== 'all') {
        params.push(`filiale_id=${filialeId}`);
      }

      if (params.length > 0) {
        endpoint += '?' + params.join('&');
      }

      const response = await http.get(endpoint);
      setPaies(response.data || []);
      
      // Récupérer les périodes intérim si la filiale traite l'intérim
      console.log('traite_interim:', currentFiliale?.traite_interim);
      if (currentFiliale?.traite_interim) {
        try {
          const interimEndpoint = '/api/paies/interim';
          const interimParams = [];
          if (!isConsolidated && filialeId && filialeId !== 'all') {
            interimParams.push(`filiale_id=${filialeId}`);
          }
          const interimUrl = interimParams.length > 0 ? `${interimEndpoint}?${interimParams.join('&')}` : interimEndpoint;
          console.log('Récupération périodes intérim depuis:', interimUrl);
          const interimResponse = await http.get(interimUrl);
          console.log('Réponse périodes intérim:', interimResponse.data);
          setPaiesInterim(interimResponse.data || []);
        } catch (error) {
          console.error('Erreur récupération paies intérim:', error);
          setPaiesInterim([]);
        }
      } else {
        console.log('La filiale ne traite pas l\'intérim');
        setPaiesInterim([]);
      }
    } catch (error) {
      console.error('Erreur récupération paies:', error);
      setPaies([]);
      setPaiesInterim([]);
    } finally {
      setLoading(false);
    }
  }, [filialeId, isConsolidated, currentFiliale?.traite_interim]);

  const getFilialeName = (paie) => {
    if (paie.filiale_libelle) return paie.filiale_libelle;
    if (paie.filiale_code) return paie.filiale_code;
    return paie.filiale_id ? `Filiale #${paie.filiale_id}` : '-';
  };

  const etatCible = ONGLETS_ETAT.find((o) => o.id === etatFiltre)?.etat ?? null;

  // Regroupe les périodes par filiale (les plus récentes en premier dans chaque filiale)
  const groupes = useMemo(() => {
    const terme = recherche.trim().toLowerCase();
    const map = new Map();
    paies.forEach((paie) => {
      if (terme && !String(paie.libelle_paie || '').toLowerCase().includes(terme)) return;
      if (etatCible !== null && paie.etat !== etatCible) return;
      const key = cleFiliale(paie);
      if (!map.has(key)) {
        map.set(key, {
          key,
          nom: key !== 'none' ? getFilialeName(paie) : 'Sans filiale',
          drapeau: paie.drapeau,
          paies: [],
        });
      }
      map.get(key).paies.push(paie);
    });
    const liste = Array.from(map.values()).map((g) => ({
      ...g,
      paies: [...g.paies].sort((a, b) => {
        const da = a.date_debut ? new Date(a.date_debut).getTime() : 0;
        const db = b.date_debut ? new Date(b.date_debut).getTime() : 0;
        return db - da || b.idpaie - a.idpaie;
      }),
      nbNonActives: g.paies.filter((p) => p.etat === 0).length,
      nbOuvertes: g.paies.filter((p) => p.etat === 1).length,
      nbCloturees: g.paies.filter((p) => p.etat === 2).length,
    }));
    return liste.sort((a, b) => a.nom.localeCompare(b.nom, 'fr'));
  }, [paies, recherche, etatCible]);

  // Regroupe les périodes intérim par filiale
  const groupesInterim = useMemo(() => {
    const map = new Map();
    paiesInterim.forEach((paie) => {
      const key = cleFiliale(paie);
      if (!map.has(key)) {
        map.set(key, {
          key,
          nom: key !== 'none' ? getFilialeName(paie) : 'Sans filiale',
          drapeau: paie.drapeau,
          paies: [],
        });
      }
      map.get(key).paies.push(paie);
    });
    const liste = Array.from(map.values()).map((g) => ({
      ...g,
      paies: [...g.paies].sort((a, b) => {
        const da = a.date_debut ? new Date(a.date_debut).getTime() : 0;
        const db = b.date_debut ? new Date(b.date_debut).getTime() : 0;
        return db - da || b.idpaie_interime - a.idpaie_interime;
      }),
      nbNonActives: g.paies.filter((p) => p.etat === 0).length,
      nbOuvertes: g.paies.filter((p) => p.etat === 1).length,
      nbCloturees: g.paies.filter((p) => p.etat === 2).length,
    }));
    console.log('groupesInterim:', liste);
    return liste.sort((a, b) => a.nom.localeCompare(b.nom, 'fr'));
  }, [paiesInterim]);

  // Au premier chargement : on déplie les filiales qui ont une période ouverte (ou la première)
  useEffect(() => {
    if (groupsInitialises.current || paies.length === 0 || groupes.length === 0) return;
    groupsInitialises.current = true;
    const init = {};
    groupes.forEach((g) => {
      init[g.key] = g.nbOuvertes > 0;
    });
    if (!Object.values(init).some(Boolean)) init[groupes[0].key] = true;
    setOpenGroups(init);
  }, [paies, groupes]);

  // En cherchant, on déplie automatiquement les filiales concernées
  const estDeplie = (key) => Boolean(recherche.trim()) || Boolean(openGroups[key]);
  const toggleGroup = (key) => setOpenGroups((prev) => ({ ...prev, [key]: !prev[key] }));
  const estDeplieInterim = (key) => Boolean(openGroupsInterim[key]);
  const toggleGroupInterim = (key) => setOpenGroupsInterim((prev) => ({ ...prev, [key]: !prev[key] }));
  const toutDeplier = () => setOpenGroups(Object.fromEntries(groupes.map((g) => [g.key, true])));
  const toutReplier = () => setOpenGroups({});

  // Choisir un onglet d'état : on déplie toutes les filiales pour voir directement les résultats
  const choisirEtat = (id) => {
    setEtatFiltre(id);
    if (id !== 'all') {
      setOpenGroups(Object.fromEntries(Array.from(new Set(paies.map(cleFiliale))).map((k) => [k, true])));
    }
  };
  const toutEstDeplie = groupes.length > 0 && groupes.every((g) => estDeplie(g.key));

  const fetchAnnees = useCallback(async () => {
    try {
      const response = await http.get('/api/annees');
      setAnnees(response.data || []);
    } catch (error) {
      console.error('Erreur récupération années:', error);
      setAnnees([]);
    }
  }, []);

  useEffect(() => { fetchPaies(); }, [fetchPaies]);
  useEffect(() => { fetchAnnees(); }, [fetchAnnees]);

  const handleCreate = (interim = false) => {
    const estInterim = interim === true;
    setEditingPaie(null);
    setIsInterim(estInterim);
    setFormData({
      libelle_paie: '',
      trimestre_id: 1,
      date_debut: '',
      date_fin: '',
      heure_tps_plein: estInterim ? 22 : 173.33,
      heure_a_payer: estInterim ? '22' : '173.33',
      jour_ferie: '',
      annee_id: '',
      filiale_id: filialeId
    });
    setShowModal(true);
  };

  const handleEdit = (paie) => {
    setEditingPaie(paie);
    // Détecter si c'est une période intérim (elle a idpaie_interime)
    const isInterimPaie = paie.idpaie_interime !== undefined;
    setIsInterim(isInterimPaie);
    setFormData({
      libelle_paie: paie.libelle_paie,
      trimestre_id: paie.trimestre_id,
      date_debut: paie.date_debut ? paie.date_debut.split('T')[0] : '',
      date_fin: paie.date_fin ? paie.date_fin.split('T')[0] : '',
      heure_tps_plein: paie.heure_tps_plein || 173.33,
      heure_a_payer: paie.heure_a_payer || '173.33',
      jour_ferie: paie.jour_ferie || '',
      annee_id: paie.annee_id || '',
      filiale_id: paie.filiale_id || filialeId
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const endpoint = isInterim ? '/api/paies/interim' : '/api/paies';
      const url = editingPaie ? `${endpoint}/${editingPaie.idpaie || editingPaie.idpaie_interime}` : endpoint;

      if (editingPaie) {
        await http.put(url, formData);
        alert(`Période de paie${isInterim ? ' intérim' : ''} modifiée avec succès`);
      } else {
        await http.post(endpoint, formData);
        alert(`Période de paie${isInterim ? ' intérim' : ''} créée avec succès`);
      }
      setShowModal(false);
      setEditingPaie(null);
      setIsInterim(false);
      fetchPaies();
    } catch (error) {
      console.error('Erreur soumission:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Erreur lors de la soumission';
      alert(errorMessage);
    }
  };

  const handleActivate = async (id) => {
    if (!window.confirm('Voulez-vous vraiment ouvrir cette période de paie ?')) {
      return;
    }
    try {
      await http.put(`/api/paies/${id}`, { etat: 1 });
      alert('Période de paie ouverte');
      fetchPaies();
    } catch (error) {
      console.error('Erreur activation:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Erreur lors de l\'activation';
      alert(errorMessage);
    }
  };

  const handleActivateInterim = async (id) => {
    if (!window.confirm('Voulez-vous vraiment ouvrir cette période de paie intérim ?')) {
      return;
    }
    try {
      await http.put(`/api/paies/interim/${id}`, { etat: 1 });
      alert('Période de paie intérim ouverte');
      fetchPaies();
    } catch (error) {
      console.error('Erreur activation intérim:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Erreur lors de l\'activation';
      alert(errorMessage);
    }
  };

  const handleSelectPaie = (id) => {
    setSelectedPaies(prev => {
      if (prev.includes(id)) {
        return prev.filter(p => p !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  // Sélection de toutes les périodes d'une filiale
  const handleSelectGroup = (groupe) => {
    const ids = groupe.paies.map((p) => p.idpaie);
    const toutesCochees = ids.every((id) => selectedPaies.includes(id));
    setSelectedPaies((prev) =>
      toutesCochees ? prev.filter((id) => !ids.includes(id)) : Array.from(new Set([...prev, ...ids]))
    );
  };

  const handleBulkActivate = async () => {
    if (selectedPaies.length === 0) {
      alert('Veuillez sélectionner au moins une période');
      return;
    }

    // Les périodes déjà ouvertes ou clôturées sont ignorées
    const ignorees = paies.filter((p) => selectedPaies.includes(p.idpaie) && p.etat !== 0);
    const toActivate = selectedPaies.filter((id) => !ignorees.find((p) => p.idpaie === id));
    if (ignorees.length > 0) {
      alert(`${ignorees.length} période(s) sont déjà ouvertes ou clôturées et seront ignorées.`);
    }
    if (toActivate.length === 0) {
      setSelectedPaies([]);
      return;
    }

    if (!window.confirm(`Voulez-vous vraiment ouvrir ${toActivate.length} période(s) de paie ?`)) {
      return;
    }

    let successCount = 0;
    let errorCount = 0;
    for (const id of toActivate) {
      try {
        await http.put(`/api/paies/${id}`, { etat: 1 });
        successCount++;
      } catch (error) {
        errorCount++;
        console.error(`Erreur activation paie ${id}:`, error);
      }
    }

    let message = `${successCount} période(s) ouverte(s) avec succès`;
    if (errorCount > 0) message += `, ${errorCount} erreur(s)`;
    alert(message);
    setSelectedPaies([]);
    fetchPaies();
  };

  const getEtatBadge = (etat) => {
    switch (etat) {
      case 0:
        return { label: 'Non active', color: 'bg-gray-100 text-gray-600' };
      case 1:
        return { label: 'Ouverte', color: 'bg-green-100 text-green-700' };
      case 2:
        return { label: 'Clôturée', color: 'bg-blue-100 text-blue-700' };
      default:
        return { label: 'Inconnu', color: 'bg-gray-100 text-gray-600' };
    }
  };

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-lg text-gray-500">Chargement...</div>
      </div>
    );
  }

  // Valeurs dérivées pour l'affichage (lecture seule de l'état existant)
  const nbOuvertesTotal = paies.filter((p) => p.etat === 1).length + paiesInterim.filter((p) => p.etat === 1).length;
  const nbNonActivesTotal = paies.filter((p) => p.etat === 0).length + paiesInterim.filter((p) => p.etat === 0).length;
  const nbClotureesTotal = paies.filter((p) => p.etat === 2).length + paiesInterim.filter((p) => p.etat === 2).length;
  const nbFiliales = new Set([...paies, ...paiesInterim].map(cleFiliale)).size;
  const totalPaies = paies.length + paiesInterim.length;
  const tauxOuvert = totalPaies ? Math.round((nbOuvertesTotal / totalPaies) * 100) : 0;
  const ongletsEtat = ONGLETS_ETAT.map((o) => ({
    ...o,
    badge: o.etat === null ? totalPaies : paies.filter((p) => p.etat === o.etat).length + paiesInterim.filter((p) => p.etat === o.etat).length,
  }));

  return (
    <div className="min-h-screen bg-gray-50">
      <PaieStyles />
      <BlueHeader
        title="Configuration des Périodes de Paie"
        subtitle="Gérer les périodes de paie par filiale"
        actions={[
          {
            label: 'Nouvelle période',
            icon: Plus,
            onClick: () => handleCreate(false),
            primary: true
          },
          ...(currentFiliale?.traite_interim ? [{
            label: 'Nouvelle période Intérim',
            icon: Plus,
            onClick: () => handleCreate(true),
            primary: true
          }] : []),
          {
            label: 'Actualiser',
            icon: RefreshCw,
            onClick: fetchPaies,
            primary: false
          }
        ]}
      />

      <div className="px-6 py-8">
        {/* Indicateurs clés */}
        <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <StatCard
            icon={Layers}
            label="Périodes"
            value={totalPaies}
            hint={`${nbFiliales} filiale${nbFiliales > 1 ? 's' : ''}`}
            gradient="from-blue-500 to-indigo-600"
            delay={0}
          />
          <StatCard
            icon={Unlock}
            label="Ouvertes"
            value={nbOuvertesTotal}
            hint={nbOuvertesTotal > 0 ? 'En cours de traitement' : 'Aucune période ouverte'}
            progress={tauxOuvert}
            gradient="from-emerald-500 to-teal-600"
            delay={60}
          />
          <StatCard
            icon={Clock}
            label="Non actives"
            value={nbNonActivesTotal}
            hint={nbNonActivesTotal > 0 ? "En attente d'ouverture" : undefined}
            gradient="from-amber-500 to-orange-600"
            delay={120}
          />
          <StatCard
            icon={Lock}
            label="Clôturées"
            value={nbClotureesTotal}
            hint={nbClotureesTotal > 0 ? 'Traitement terminé' : undefined}
            gradient="from-sky-500 to-blue-600"
            delay={180}
          />
        </div>

        {/* Barre d'outils */}
        <div className="mb-5 overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-black/5">
          <div className="flex flex-col gap-3 p-4 md:flex-row md:items-center md:justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-800">Périodes de paie</h3>
              <p className="mt-0.5 text-sm text-gray-500">
                {paies.length} période(s) réparties sur {groupes.length} filiale(s)
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={recherche}
                  onChange={(e) => setRecherche(e.target.value)}
                  placeholder="Rechercher une période…"
                  className="w-56 rounded-xl border border-gray-200 bg-gray-50 py-2 pl-9 pr-8 text-sm transition-all focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                />
                {recherche && (
                  <button
                    type="button"
                    onClick={() => setRecherche('')}
                    aria-label="Effacer la recherche"
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
              <button
                type="button"
                onClick={toutEstDeplie ? toutReplier : toutDeplier}
                className="flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 transition-colors hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700"
              >
                {toutEstDeplie ? <ChevronsDownUp className="h-4 w-4" /> : <ChevronsUpDown className="h-4 w-4" />}
                {toutEstDeplie ? 'Tout replier' : 'Tout déplier'}
              </button>
              {selectedPaies.length > 0 && (
                <button
                  onClick={handleBulkActivate}
                  className="paie-fade flex items-center gap-2 rounded-xl bg-gradient-to-r from-emerald-500 to-green-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md active:translate-y-0"
                >
                  <Play className="h-4 w-4" />
                  Activer ({selectedPaies.length})
                </button>
              )}
            </div>
          </div>
          <div className="border-t border-gray-100 bg-gray-50/60 px-4 py-3">
            <div className="max-w-2xl">
              <SlidingTabs tabs={ongletsEtat} active={etatFiltre} onChange={choisirEtat} label="Filtrer par état de la période" />
            </div>
          </div>
        </div>

        {/* Sections par filiale */}
        {groupes.length === 0 ? (
          <div className="paie-fade-up rounded-2xl bg-white px-6 py-16 text-center text-gray-500 shadow-sm ring-1 ring-black/5">
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
              <Layers className="h-6 w-6 text-gray-400" />
            </div>
            {recherche
              ? 'Aucune période ne correspond à votre recherche'
              : etatFiltre !== 'all'
                ? 'Aucune période dans cet état'
                : 'Aucune période de paie trouvée'}
          </div>
        ) : (
          <div className="space-y-4">
            {groupes.map((groupe, gi) => {
              const ouvert = estDeplie(groupe.key);
              const ids = groupe.paies.map((p) => p.idpaie);
              const nbCochees = ids.filter((id) => selectedPaies.includes(id)).length;
              const panelId = `filiale-panel-${idDom(groupe.key)}`;
              return (
                <section
                  key={groupe.key}
                  className={`paie-fade-up overflow-hidden rounded-2xl border-l-4 bg-white shadow-sm ring-1 transition-shadow duration-200 ${
                    groupe.nbOuvertes > 0 ? 'border-l-green-500' : 'border-l-transparent'
                  } ${ouvert ? 'shadow-md ring-blue-200' : 'ring-black/5 hover:shadow-md'}`}
                  style={{ animationDelay: `${Math.min(gi, 8) * 50}ms` }}
                >
                  {/* En-tête cliquable */}
                  <div
                    role="button"
                    tabIndex={0}
                    aria-expanded={ouvert}
                    aria-controls={panelId}
                    onClick={() => toggleGroup(groupe.key)}
                    onKeyDown={(e) => {
                      if (e.target !== e.currentTarget) return;
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggleGroup(groupe.key);
                      }
                    }}
                    className={`flex cursor-pointer select-none items-center gap-4 px-5 py-4 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500 ${
                      ouvert ? 'bg-gradient-to-r from-blue-50 to-white' : 'hover:bg-gray-50'
                    }`}
                  >
                    <TriCheckbox
                      checked={nbCochees === ids.length && ids.length > 0}
                      indeterminate={nbCochees > 0 && nbCochees < ids.length}
                      onChange={() => handleSelectGroup(groupe)}
                      label={`Sélectionner toutes les périodes de ${groupe.nom}`}
                    />
                    <FilialeFlag src={groupe.drapeau} alt={`Drapeau ${groupe.nom}`} />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-base font-semibold text-gray-900">{groupe.nom}</div>
                      <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs">
                        <span className="rounded-full bg-gray-100 px-2.5 py-0.5 font-medium text-gray-700">
                          {groupe.paies.length} période{groupe.paies.length > 1 ? 's' : ''}
                        </span>
                        {groupe.nbOuvertes > 0 && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2.5 py-0.5 font-medium text-green-700">
                            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-green-500" />
                            {groupe.nbOuvertes} ouverte{groupe.nbOuvertes > 1 ? 's' : ''}
                          </span>
                        )}
                        {groupe.nbNonActives > 0 && (
                          <span className="rounded-full bg-amber-50 px-2.5 py-0.5 font-medium text-amber-700">
                            {groupe.nbNonActives} non active{groupe.nbNonActives > 1 ? 's' : ''}
                          </span>
                        )}
                        {groupe.nbCloturees > 0 && (
                          <span className="rounded-full bg-blue-50 px-2.5 py-0.5 font-medium text-blue-700">
                            {groupe.nbCloturees} clôturée{groupe.nbCloturees > 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="hidden sm:block">
                      <RepartitionEtats ouvertes={groupe.nbOuvertes} nonActives={groupe.nbNonActives} cloturees={groupe.nbCloturees} />
                    </div>
                    <ChevronDown
                      className={`h-5 w-5 flex-shrink-0 text-gray-400 transition-transform duration-300 ${ouvert ? 'rotate-180 text-blue-600' : ''}`}
                    />
                  </div>

                  {/* Contenu dépliable (animation de hauteur) */}
                  <div
                    id={panelId}
                    className={`grid transition-[grid-template-rows] duration-300 ease-out motion-reduce:transition-none ${
                      ouvert ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                    }`}
                  >
                    <div
                      className="min-h-0 overflow-hidden"
                      aria-hidden={!ouvert}
                      style={{
                        visibility: ouvert ? 'visible' : 'hidden',
                        transition: ouvert ? 'visibility 0s' : 'visibility 0s linear 300ms',
                      }}
                    >
                      <div className="overflow-x-auto border-t border-gray-100">
                        <table className="w-full">
                          <thead className="bg-gray-50">
                            <tr>
                              {['', '#', 'Libellé', 'Début', 'Fin', 'Trim.', 'H. temps plein', 'H. à payer', 'État'].map((h, i) => (
                                <th
                                  key={`${h}-${i}`}
                                  className="px-5 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-gray-500"
                                >
                                  {h}
                                </th>
                              ))}
                              <th className="px-5 py-2.5 text-center text-[11px] font-semibold uppercase tracking-wider text-gray-500">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100 bg-white">
                            {groupe.paies.map((paie, index) => {
                              const badge = getEtatBadge(paie.etat);
                              const duree = dureeJours(paie.date_debut, paie.date_fin);
                              return (
                                <tr key={paie.idpaie} className="transition-colors hover:bg-blue-50/40">
                                  <td className={`whitespace-nowrap px-5 py-3.5 ${paie.etat === 1 ? 'shadow-[inset_3px_0_0_0_#22c55e]' : ''}`}>
                                    <input
                                      type="checkbox"
                                      aria-label={`Sélectionner ${paie.libelle_paie}`}
                                      checked={selectedPaies.includes(paie.idpaie)}
                                      onChange={() => handleSelectPaie(paie.idpaie)}
                                      className="h-4 w-4 cursor-pointer rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                    />
                                  </td>
                                  <td className="whitespace-nowrap px-5 py-3.5 text-sm text-gray-400 tabular-nums">{index + 1}</td>
                                  <td className="whitespace-nowrap px-5 py-3.5 text-sm font-semibold text-gray-900">{paie.libelle_paie}</td>
                                  <td className="whitespace-nowrap px-5 py-3.5 text-sm tabular-nums text-gray-600">
                                    {paie.date_debut ? new Date(paie.date_debut).toLocaleDateString('fr-FR') : '-'}
                                  </td>
                                  <td className="whitespace-nowrap px-5 py-3.5 text-sm tabular-nums text-gray-600">
                                    {paie.date_fin ? new Date(paie.date_fin).toLocaleDateString('fr-FR') : '-'}
                                    {duree && duree > 0 ? (
                                      <span className="ml-2 rounded-md bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-500">{duree} j</span>
                                    ) : null}
                                  </td>
                                  <td className="whitespace-nowrap px-5 py-3.5 text-sm text-gray-900">
                                    <span className="rounded-md bg-indigo-50 px-2 py-0.5 text-xs font-semibold text-indigo-700">{paie.trimestre_id}</span>
                                  </td>
                                  <td className="whitespace-nowrap px-5 py-3.5 text-sm tabular-nums text-gray-900">{paie.heure_tps_plein}</td>
                                  <td className="whitespace-nowrap px-5 py-3.5 text-sm tabular-nums text-gray-900">{paie.heure_a_payer}</td>
                                  <td className="whitespace-nowrap px-5 py-3.5 text-sm">
                                    <div className="flex flex-col">
                                      <span className={`inline-flex w-fit items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${badge.color}`}>
                                        <span className={`h-1.5 w-1.5 rounded-full ${ETAT_DOT[paie.etat] || 'bg-gray-400'} ${paie.etat === 1 ? 'animate-pulse' : ''}`} />
                                        {badge.label}
                                      </span>
                                      {(paie.approuve_par || paie.cloture_par) && (
                                        <span className="mt-1 text-xs text-gray-500">
                                          {paie.approuve_par && `App: ${paie.approuve_par}`}
                                          {paie.approuve_par && paie.cloture_par && ' • '}
                                          {paie.cloture_par && `Cl: ${paie.cloture_par}`}
                                        </span>
                                      )}
                                    </div>
                                  </td>
                                  <td className="whitespace-nowrap px-5 py-3.5 text-sm">
                                    <div className="flex justify-center gap-1">
                                      {paie.etat === 0 && (
                                        <button
                                          onClick={() => handleActivate(paie.idpaie)}
                                          className="rounded-lg p-2 text-green-600 transition-all hover:scale-110 hover:bg-green-50 hover:text-green-800"
                                          title="Ouvrir"
                                          aria-label={`Ouvrir ${paie.libelle_paie}`}
                                        >
                                          <Unlock className="h-4 w-4" />
                                        </button>
                                      )}
                                      {paie.etat === 2 && (
                                        <>
                                          <button
                                            onClick={() => navigate(`/reporting-paie?type=normal&date=${paie.date_debut}`)}
                                            className="rounded-lg p-2 text-blue-600 transition-all hover:scale-110 hover:bg-blue-50 hover:text-blue-800"
                                            title="Consulter le reporting de cette période"
                                            aria-label={`Reporting ${paie.libelle_paie}`}
                                          >
                                            <FileText className="h-4 w-4" />
                                          </button>
                                          <button
                                            onClick={() => navigate(`/reporting-performance?date=${paie.date_debut}`)}
                                            className="rounded-lg p-2 text-emerald-600 transition-all hover:scale-110 hover:bg-emerald-50 hover:text-emerald-800"
                                            title="Consulter le reporting performance de cette période"
                                            aria-label={`Reporting performance ${paie.libelle_paie}`}
                                          >
                                            <BarChart3 className="h-4 w-4" />
                                          </button>
                                        </>
                                      )}
                                      <button
                                        onClick={() => handleEdit(paie)}
                                        className="rounded-lg p-2 text-gray-600 transition-all hover:scale-110 hover:bg-gray-100 hover:text-gray-800 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:scale-100 disabled:hover:bg-transparent"
                                        title={paie.etat !== 0 ? 'Modification impossible : période déjà ouverte ou clôturée' : 'Modifier'}
                                        aria-label={`Modifier ${paie.libelle_paie}`}
                                        disabled={paie.etat !== 0}
                                      >
                                        <Calendar className="h-4 w-4" />
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </section>
              );
            })}
          </div>
        )}

        {/* Section Périodes Intérim - affichée uniquement si la filiale traite l'intérim */}
        {(currentFiliale?.traite_interim || groupesInterim.length > 0) && (
          <div className="mt-8">
            <div className="mb-4 flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-purple-500 to-pink-600 text-white shadow-lg">
                <Building2 className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900">Périodes de Paie Intérim</h2>
                <p className="text-sm text-gray-500">Gestion des périodes pour l'activité intérim</p>
              </div>
            </div>

            {groupesInterim.length === 0 ? (
              <div className="paie-fade-up rounded-2xl bg-white px-6 py-16 text-center text-gray-500 shadow-sm ring-1 ring-black/5">
                <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
                  <Calendar className="h-6 w-6 text-gray-400" />
                </div>
                Aucune période de paie intérim trouvée
              </div>
            ) : (
              <div className="space-y-4">
                {groupesInterim.map((groupe, gi) => {
                  const ouvert = estDeplieInterim(groupe.key);
                  const panelId = `filiale-interim-panel-${idDom(groupe.key)}`;
                  return (
                    <section
                      key={groupe.key}
                      className={`paie-fade-up overflow-hidden rounded-2xl border-l-4 bg-white shadow-sm ring-1 transition-shadow duration-200 ${
                        groupe.nbOuvertes > 0 ? 'border-l-purple-500' : 'border-l-transparent'
                      } ${ouvert ? 'shadow-md ring-purple-200' : 'ring-black/5 hover:shadow-md'}`}
                      style={{ animationDelay: `${Math.min(gi, 8) * 50}ms` }}
                    >
                      <div
                        role="button"
                        tabIndex={0}
                        aria-expanded={ouvert}
                        aria-controls={panelId}
                        onClick={() => toggleGroupInterim(groupe.key)}
                        className={`flex cursor-pointer select-none items-center gap-4 px-5 py-4 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-purple-500 ${
                          ouvert ? 'bg-gradient-to-r from-purple-50 to-white' : 'hover:bg-gray-50'
                        }`}
                      >
                        <FilialeFlag src={groupe.drapeau} alt={groupe.nom} />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-gray-900">{groupe.nom}</span>
                            <span className="rounded-full bg-purple-100 px-2 py-0.5 text-xs font-semibold text-purple-700">Intérim</span>
                          </div>
                          <div className="text-xs text-gray-500">
                            {groupe.paies.length} période{groupe.paies.length > 1 ? 's' : ''}
                          </div>
                        </div>
                        <RepartitionEtats
                          ouvertes={groupe.nbOuvertes}
                          nonActives={groupe.nbNonActives}
                          cloturees={groupe.nbCloturees}
                        />
                        <ChevronDown
                          className={`h-5 w-5 text-gray-400 transition-transform duration-200 ${ouvert ? 'rotate-180' : ''}`}
                        />
                      </div>

                      <div id={panelId} role="tabpanel" aria-labelledby={panelId} className={ouvert ? 'paie-fade' : 'hidden'}>
                        <div className="px-5 pb-5">
                          <table className="w-full text-sm">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-5 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Période</th>
                                <th className="px-5 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Dates</th>
                                <th className="px-5 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Heures</th>
                                <th className="px-5 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">État</th>
                                <th className="px-5 py-2.5 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {groupe.paies.map((paie) => (
                                <tr key={paie.idpaie_interime} className="hover:bg-purple-50/30">
                                  <td className="px-5 py-3.5">
                                    <div className="font-medium text-gray-900">{paie.libelle_paie}</div>
                                    <div className="text-xs text-gray-500">T{paie.trimestre_id}</div>
                                  </td>
                                  <td className="px-5 py-3.5 text-gray-600">
                                    <div>{formatDateFr(paie.date_debut)} → {formatDateFr(paie.date_fin)}</div>
                                  </td>
                                  <td className="px-5 py-3.5 text-gray-600">
                                    <div>TP: {paie.heure_tps_plein}j</div>
                                    <div>AP: {paie.heure_a_payer}j</div>
                                  </td>
                                  <td className="px-5 py-3.5">
                                    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                                      paie.etat === 0 ? 'bg-gray-100 text-gray-600' :
                                      paie.etat === 1 ? 'bg-green-100 text-green-700' :
                                      'bg-blue-100 text-blue-700'
                                    }`}>
                                      {paie.etat === 0 ? 'Non active' : paie.etat === 1 ? 'Ouverte' : 'Clôturée'}
                                    </span>
                                  </td>
                                  <td className="whitespace-nowrap px-5 py-3.5 text-sm">
                                    <div className="flex justify-center gap-1">
                                      {paie.etat === 0 && (
                                        <button
                                          onClick={() => handleActivateInterim(paie.idpaie_interime)}
                                          className="rounded-lg p-2 text-green-600 transition-all hover:scale-110 hover:bg-green-50 hover:text-green-800"
                                          title="Ouvrir"
                                          aria-label={`Ouvrir ${paie.libelle_paie}`}
                                        >
                                          <Unlock className="h-4 w-4" />
                                        </button>
                                      )}
                                      {paie.etat === 2 && (
                                        <button
                                          onClick={() => navigate(`/reporting-paie?type=interim&date=${paie.date_debut}`)}
                                          className="rounded-lg p-2 text-purple-600 transition-all hover:scale-110 hover:bg-purple-50 hover:text-purple-800"
                                          title="Consulter le reporting de cette période"
                                          aria-label={`Reporting ${paie.libelle_paie}`}
                                        >
                                          <FileText className="h-4 w-4" />
                                        </button>
                                      )}
                                      <button
                                        onClick={() => handleEdit({ ...paie, idpaie: paie.idpaie_interime })}
                                        className="rounded-lg p-2 text-purple-600 transition-all hover:scale-110 hover:bg-purple-50 hover:text-purple-800"
                                        title="Modifier"
                                      >
                                        <Calendar className="h-4 w-4" />
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </section>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal création/modification */}
      {showModal && (
        <PeriodeModal
          formData={formData}
          setFormData={setFormData}
          annees={annees}
          editing={Boolean(editingPaie)}
          etatLabel={editingPaie ? getEtatBadge(editingPaie.etat).label : undefined}
          filialeCode={editingPaie ? normaliserCode(editingPaie.filiale_code) : (!isConsolidated ? normaliserCode(currentFiliale?.code) : '')}
          isInterim={isInterim}
          onSubmit={handleSubmit}
          onClose={() => {
            setShowModal(false);
            setIsInterim(false);
          }}
        />
      )}
    </div>
  );
};

export default ConfigPaie;